import { InlineNotificationProps } from '@moneyforward/ap-frontend-components';
import {
  CommonError,
  InvoiceReportDetailResponseFlashMessagesItemType,
} from 'ap-openapi';
import { Dayjs } from 'dayjs';
import { FieldError } from 'react-hook-form';

export type ApiErrors = {
  invoiceReport: string | undefined;
  invoiceTransactions: string | undefined;
  invoiceTransactionsAlert: string | undefined;
  invoiceTransactionsRow: InvoiceTransactionErrorType | undefined;
};

export type ErrorField = Record<string, FieldError>;
export type ErrorFields = Record<string, FieldError | ErrorField>;
export const convertFlashMessageTypeToInlineNotificationType = (
  flashMessageType: InvoiceReportDetailResponseFlashMessagesItemType | undefined
): InlineNotificationProps['type'] => {
  switch (flashMessageType) {
    case 'error':
      return 'error';
    case 'warning':
      return 'alert';
    case 'success':
    case 'info':
      return 'success';
    default:
      return 'error';
  }
};

export type InvoiceTransactionErrorType = CommonError & { index?: number };

export type OcrInferenceResult = {
  reference_report?: {
    id?: string;
    number?: string;
    title?: string;
  };
  dueDate?: Dayjs;
  bookDate?: Dayjs;
  invoiceRegistrationNumber?: string;
  apPayee?: InferencePayee;
  apInstantPayee?: InferenceInstantPayee;
  invoiceTransactions: InferenceInvoiceTransaction[];
  inferences: Inference[];
  exciseValue?: number;
  withholdingIncomeTaxValue?: number;
  paymentRequestTotalValue?: number;
  transactionDetailTotalValue?: number;
  subtotalTaxFree?: {
    totalValue?: number;
    totalValueWithoutTax?: number;
    exciseValue?: number;
  };
  subtotalUntaxable?: {
    totalValue?: number;
    totalValueWithoutTax?: number;
    exciseValue?: number;
  };
  subtotal10?: {
    totalValue?: number;
    totalValueWithoutTax?: number;
    exciseValue?: number;
  };
  subtotal8?: {
    totalValue?: number;
    totalValueWithoutTax?: number;
    exciseValue?: number;
  };
  title?: string;
};

type InferencePayee = {
  id?: string;
  name?: string;
  code?: string;
  bookDate?: Dayjs;
  paymentDate?: Dayjs;
  displayName?: string;
  defaultName?: string;
  defaultExItem?: {
    id?: string;
    displayName?: string;
  };
  defaultCrItem?: {
    id?: string;
    displayName?: string;
  };
  defaultCrSubItem?: {
    id?: string;
    displayName?: string;
  };
  defaultDept?: {
    id?: string;
    displayName?: string;
  };
  defaultProject?: {
    id?: string;
    displayName?: string;
  };
  isTaxIncluded?: boolean;
  isWithholdingTax?: boolean;
  invoiceRegistrationNumber?: string;
  paymentMethod?: string;
  foreignCurrency?: {
    currency?: string;
    rates: {
      startDate?: Dayjs;
      endDate?: Dayjs;
      value?: number;
    }[];
  };
  contactOfficeMembers?: {
    id?: string;
    name?: string;
    displayName?: string;
  }[];
};

type InferenceInstantPayee = {
  name?: string;
  holderNameKana?: string;
  accountType?: string;
  number?: string;
  bank?: {
    id?: string;
    name?: string;
  };
  bankBranch?: {
    id?: string;
    name?: string;
  };
};

type InferenceInvoiceTransaction = {
  number?: string;
  name?: string;
  unitValue?: number;
  quantity?: number;
  totalValue?: number;
  totalTaxIncludeValue?: number;
  exciseValue?: number;
  hasWithholdingIncomeTax?: boolean;
  withholdingIncomeTaxValue?: number;
  memo?: string;
  currency?: string;
  jpyRate?: number;
  useCustomJpyRate?: boolean;
  apPayee?: {
    id?: string;
    name?: string;
  };
  apPayeeType?: string;
  dept?: {
    id?: string;
    name?: string;
  };
  project?: {
    id?: string;
    name?: string;
  };
  exItem?: {
    id?: string;
    name?: string;
  };
  drExcise?: {
    id?: string;
    name?: string;
  };
  crItem?: {
    id?: string;
    name?: string;
  };
  crSubItem?: {
    id?: string;
    name?: string;
  };
  invoiceKind?: string;
  dealDate?: Dayjs;
};

export type Inference = {
  title?: InferenceDetail;
  apPayee?: InferenceDetail;
  dueDate?: InferenceDetail;
  bookDate?: InferenceDetail;
  invoiceRegistrationNumber?: InferenceDetail;
  invoiceTransactions: {
    dealDate?: InferenceDetail;
    name?: InferenceDetail;
    dept?: InferenceDetail;
    exItem?: InferenceDetail;
    project?: InferenceDetail;
    crItem?: InferenceDetail;
    crSubItem?: InferenceDetail;
  }[];
};

export type InferenceDetail = {
  source: string | null;
  score?: number;
  reason?: string;
  position?: InferencePosition;
};

type InferencePosition = {
  leftTop?: InferencePositionValue;
  rightTop?: InferencePositionValue;
  leftBottom?: InferencePositionValue;
  rightBottom?: InferencePositionValue;
};

type InferencePositionValue = {
  x?: number;
  y?: number;
};
